/**
 * Learning Moments — the curated coaching catalogue and the problem-type graph
 * behind it, under one nav entry.
 *
 * Both answer "what shapes of difficulty do we recognise, and what do we say
 * about them", so they belong side by side rather than several entries apart in
 * a sidebar that had grown past the fold.
 *
 * Thin by design: it owns which tab shows and nothing else, so each tab keeps
 * the state it already had. The deeper Learning Moments pages (categories, runs,
 * taxonomy, encounters) stay their own routes — they are drill-downs reached
 * from inside the page, not peers of it.
 *
 * NB Learning Moments (curated, authored here) is not Sticking Patterns
 * (emergent, produced by analysis). They are separate systems and separate nav
 * entries; nothing here touches Patterns.
 */

import { useSearchParams } from 'react-router-dom';
import { TabStrip } from '@shared-ui/components';
import { LearningMoments } from './LearningMoments';
import { ProblemTypesList } from './ProblemTypesList';

type TabKey = 'moments' | 'problem-types';

const DEFAULT_TAB: TabKey = 'moments';

const TABS = [
  { key: 'moments' as const, label: 'Learning Moments' },
  { key: 'problem-types' as const, label: 'Problem Types' },
];

export function LearningMomentsHost() {
  const [searchParams, setSearchParams] = useSearchParams();

  const requested = searchParams.get('tab') as TabKey | null;
  // An unknown tab falls back rather than rendering blank, so a stale link
  // still lands somewhere real.
  const active: TabKey = TABS.some(t => t.key === requested) ? requested! : DEFAULT_TAB;

  const selectTab = (key: TabKey): void => {
    // `replace` so Back leaves the page rather than walking every tab clicked
    // on the way here.
    setSearchParams(key === DEFAULT_TAB ? {} : { tab: key }, { replace: true });
  };

  return (
    <div className="learning-moments-host">
      <TabStrip
        ariaLabel="Learning Moments views"
        tabs={TABS}
        active={active}
        onChange={selectTab}
      />
      {active === 'moments' && <LearningMoments />}
      {active === 'problem-types' && <ProblemTypesList />}
    </div>
  );
}
